import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language } from '@/lib/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('he');

  useEffect(() => {
    // Hebrew-primary site (all content is Hebrew RTL): default to Hebrew for
    // everyone — only honor an explicit saved preference. (Previously defaulted
    // non-Hebrew browsers to 'en'/ltr, which broke RTL layout for foreign
    // visitors and made Lighthouse/Googlebot see lang=en on a Hebrew site.)
    const savedLang = localStorage.getItem('preferred-language') as Language;
    const initialLang = savedLang || 'he';

    setLanguage(initialLang);
    
    // Set document direction
    document.dir = initialLang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = initialLang;
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('preferred-language', lang);
    document.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  const isRTL = language === 'he';

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};